import { useMemo, useState } from 'react'

import { PaginationState } from '@tanstack/react-table'
import { useUnit } from 'effector-react'
import { useNavigate, useParams } from 'react-router'

import { Page } from '@/components/layout/page/page.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { $creatorId } from '@/models/creators.ts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { usePpvTracking } from '@/modules/tracking/ppv/hooks/usePpvTracking.tsx'
import { usePpvTrackingMessagesQuery } from '@/modules/tracking/ppv/hooks/usePpvTrackingMessagesQuery.tsx'
import { PpvMessagesTable } from '@/modules/tracking/ppv/ui/messages/PpvMessagesTable.tsx'
import { PpvMessagesTopPageComponents } from '@/modules/tracking/ppv/ui/messages/PpvMessagesTopPageComponents.tsx'
import { AppRoutes } from '@/utils/constants.ts'
import { useCreatorUrl } from '@/utils/creators.ts'

export const PpvTrackingMessagesPage = () => {
  const creatorId = useUnit($creatorId) ?? ''
  const { userSettings } = useAccount()
  const shouldShowCents = useMemo(
    () => userSettings?.showCents,
    [userSettings?.showCents],
  )
  const { categoryIndex, mediaSetIndex } = useParams()
  const { date, allCategories, isFetching } = usePpvTracking()
  const navigate = useNavigate()
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const {
    data: allMessages,
    isFetching: messagesFetching,
    isIdle: messagesIdle,
    refetch: refetchCurrentPage,
  } = usePpvTrackingMessagesQuery({
    date,
    categoryIndex: categoryIndex!,
    mediaSetIndex: mediaSetIndex!,
    pagination,
  })

  const loadingPage = (
    <Page
      className="mb-6"
      title="Loading messages..."
      titleClassName="text-3xl"
    >
      <Skeleton className="h-80 w-full" />
    </Page>
  )

  const ppvListUrl = useCreatorUrl(AppRoutes.Creator.ChatterTracking.Ppv.List)

  if (!allCategories || isFetching || messagesFetching || messagesIdle) {
    return loadingPage
  }

  if (!allMessages) {
    return loadingPage
  }

  if (!categoryIndex || allCategories.length < parseInt(categoryIndex) + 1) {
    navigate(ppvListUrl)

    return
  }

  const category = allCategories[parseInt(categoryIndex)]

  if (
    !mediaSetIndex ||
    category.mediaSets.length < parseInt(mediaSetIndex) + 1
  ) {
    navigate(
      AppRoutes.Creator.ChatterTracking.Ppv.Details.asUrl(
        creatorId,
        categoryIndex,
      ),
    )

    return
  }

  const mediaSet = category.mediaSets[parseInt(mediaSetIndex)]

  return (
    <Page
      above={
        <PpvMessagesTopPageComponents
          showCents={shouldShowCents}
          category={category}
          mediaSet={mediaSet}
        />
      }
    >
      <PpvMessagesTable
        totalMessages={mediaSet.totalMessages}
        messages={allMessages}
        showCents={shouldShowCents}
        pagination={pagination}
        setPagination={setPagination}
        refetchCurrentPage={refetchCurrentPage}
        categoryIndex={categoryIndex}
        mediaSetIndex={mediaSetIndex}
      />
    </Page>
  )
}
